import React from 'react';
import { MessageCitation } from '@/types/chat';
import {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../../components/motion-primitives/accordion';
import { AccordionTriggerView } from '../ui/AccordionTriggerView';
import { AccordionContentView } from '../ui/AccordionContentView';
import { useCitationStore } from '@/stores/citationStore';
import { normalizeUrl } from '@/lib/urlNormalize';

interface CanvasItemProps {
  item: MessageCitation;
  index?: number;
  isHighlighted?: boolean;
  onClick?: () => void;
  normalizedUrl?: string; // Pre-normalized URL for AccordionItem value
}

export const CanvasItem = React.forwardRef<HTMLDivElement, CanvasItemProps>(({ item, index, isHighlighted: propIsHighlighted, onClick, normalizedUrl: propNormalizedUrl }, ref) => {
  const { setExpandedUrl, setSelectedUrl, selectedUrl, activeUrl, expandedUrl } = useCitationStore();
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const delay = 150; // Same delay as IdeaCards

  // Use provided normalized URL or compute it
  const normalizedItemUrl = React.useMemo(() =>
    propNormalizedUrl || normalizeUrl(item.url),
    [propNormalizedUrl, item.url]
  );

  // Determine if this item should be highlighted
  // Priority: activeUrl (LinkPill hover) > selectedUrl (clicked) > expandedUrl (CanvasItem hover)
  const effectiveExpandedUrl = activeUrl || selectedUrl || expandedUrl;

  const isHighlighted = propIsHighlighted !== undefined
    ? propIsHighlighted
    : normalizedItemUrl === effectiveExpandedUrl;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      // Only set expandedUrl if this item isn't already selected (clicked)
      // Selected items persist, so we don't override with hover
      // selectedUrl is already normalized when stored, so compare directly
      if (selectedUrl !== normalizedItemUrl) {
        setExpandedUrl(normalizedItemUrl);
      }
    }, delay);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    // Clear expandedUrl on mouse leave, but only if this item wasn't clicked
    // Selected items persist via selectedUrl
    // selectedUrl is already normalized when stored, so compare directly
    if (selectedUrl !== normalizedItemUrl) {
      setExpandedUrl(null);
    }
  };

  // Use a local ref for scrolling
  const itemRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, [selectedUrl, item.url]);

  // Auto-scroll to center when this item becomes highlighted
  React.useEffect(() => {
    if (isHighlighted && itemRef.current) {
      // Small delay to ensure accordion expansion animation has started
      const scrollTimeout = setTimeout(() => {
        itemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
  }, [isHighlighted]);

  return (
    <div
      ref={(node) => {
        itemRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && node) {
          Object.assign(ref, { current: node });
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AccordionItem value={normalizedItemUrl}>
        <AccordionTrigger className='w-full py-0.5 text-left text-zinc-950 dark:text-zinc-50'>
          <AccordionTriggerView title={item.title} url={item.url} index={index} isHighlighted={isHighlighted} />
        </AccordionTrigger>
        <AccordionContent className='origin-left'>
          <AccordionContentView url={item.url} title={item.title} />
        </AccordionContent>
      </AccordionItem>
    </div>
  );
});

CanvasItem.displayName = 'CanvasItem';
